import {
  autoUpdate,
  flip,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
} from '@floating-ui/react';
import {
  ChangeEvent,
  HTMLAttributes,
  RefCallback,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import useAutocompleteDebounce from '../hooks/useAutocompleteDebounce';

export interface ReferenceProps extends HTMLAttributes<Element> {
  ref?: RefCallback<Element>;
}

export interface FloatingProps extends HTMLAttributes<HTMLElement> {
  ref?: RefCallback<HTMLElement>;
}

export interface ItemProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  ref?: RefCallback<HTMLElement>;
  'data-active': true | undefined;
  active: boolean;
}

interface ItemOptions {
  stayOpenOnSelect?: boolean;
}

const useAutoComplete = <TValue extends unknown = unknown>(
  onSelected: (index: number, value?: TValue) => void,
  openOptions?: {
    openOnFocus?: boolean;
    stayOpenOnEmptySearch?: boolean;
    fixedWidth?: boolean;
    fixedHeight?: boolean;
    stayOpenOnOptionClick?: boolean;
    skipFocusOnOptionClick?: boolean;
    onPortalClose?: () => void;
  },
  inputAutocompleteString?: string,
  initialInputValue?: string
) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(initialInputValue ?? '');
  const [debouncedInputValue, setDebouncedInputValue] = useAutocompleteDebounce(
    inputValue,
    200
  );
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Clears the keyboard highlight. Callers use this when the rendered row set
  // changes for a reason other than typing (e.g. expanding a group), so a stale
  // activeIndex can't point Enter at a different row than the one shown active.
  const resetActiveIndex = useCallback(() => setActiveIndex(null), []);

  const listRef = useRef<Array<HTMLElement | null>>([]);

  const valueRef = useRef<Array<TValue | undefined>>([]);
  const itemOptionsRef = useRef<Array<ItemOptions | undefined>>([]);

  const id = useId();

  useLayoutEffect(() => {
    // IMPORTANT: When the floating element first opens, this runs when the
    // styles have **not yet** been applied to the element. This can cause an
    // infinite loop as `size` has not yet limited the maxHeight, so the whole
    // page tries to scroll. We must wrap it in rAF.
    requestAnimationFrame(() => {
      if (activeIndex != null) {
        listRef.current[activeIndex]?.scrollIntoView({block: 'nearest'});
      }
    });
  }, [activeIndex]);

  const {x, y, strategy, context, refs} = useFloating<HTMLElement>({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: (o) => {
      setOpen(o);
      if (!o) {
        openOptions?.onPortalClose?.();
      }
    },
    placement: 'bottom-start',
    middleware: [
      size({
        apply({rects, availableHeight, elements}) {
          Object.assign(elements.floating.style, {
            width: !openOptions?.fixedWidth
              ? `${rects.reference.width}px`
              : undefined,
            maxHeight: !openOptions?.fixedHeight
              ? `${availableHeight}px`
              : undefined,
          });
        },
        padding: 16,
      }),
      flip(),
    ],
  });

  const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([
    useDismiss(context),
    useListNavigation(context, {
      listRef,
      activeIndex,
      onNavigate: setActiveIndex,
      virtual: true,
      loop: true,
    }),
  ]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);

    if (value) {
      setOpen(true);
      setActiveIndex(0);
    } else if (openOptions?.stayOpenOnEmptySearch) {
      setActiveIndex(null);
    } else {
      setOpen(false);
    }
  };

  const getReferencePropsWithArgs = (): ReferenceProps =>
    getReferenceProps({
      onChange,
      value: inputValue,
      'aria-autocomplete': 'list',
      autoComplete: inputAutocompleteString || 'off',
      onFocus: openOptions?.openOnFocus ? () => setOpen(true) : undefined,
      onKeyDown: (e) => {
        if (e.key === 'Enter' && activeIndex != null) {
          e.preventDefault();
          e.stopPropagation();
          onSelected(activeIndex, valueRef.current[activeIndex]);

          const itemOptions = itemOptionsRef.current[activeIndex];
          if (itemOptions?.stayOpenOnSelect) {
            setActiveIndex(null);
          } else if (!openOptions?.stayOpenOnOptionClick) {
            setActiveIndex(null);
            setOpen(false);
          }
        }

        if (e.key === 'Tab') {
          setOpen(false);
        }
      },
    });

  const getFloatingPropsWithArgs = (): FloatingProps =>
    getFloatingProps({
      ref: refs.setFloating,
      style: {
        position: strategy,
        left: x ?? 0,
        top: y ?? 0,
      },
    });

  const getItemPropsWithArgs = (
    index: number,
    value?: TValue,
    itemOptions?: ItemOptions
  ): ItemProps => ({
    ...getItemProps({
      key: index,
      ref(node) {
        listRef.current[index] = node;
        valueRef.current[index] = value;
        itemOptionsRef.current[index] = itemOptions;
      },
      onClick() {
        onSelected(index, valueRef.current[index]);
        if (itemOptions?.stayOpenOnSelect) {
          setActiveIndex(null);
        } else if (!openOptions?.stayOpenOnOptionClick) {
          setOpen(false);
        }
        if (!openOptions?.skipFocusOnOptionClick) {
          refs.domReference.current?.focus();
        }
      },
      id: `${id}-${index}`,
    }),
    active: activeIndex === index,
    'data-active': activeIndex === index || undefined,
  });

  const resetSearch = useCallback((value: string) => {
    setInputValue(value);
    setDebouncedInputValue(value);
  }, []);

  return {
    getReferenceProps: getReferencePropsWithArgs,
    getInputProps: () => ({
      ...getReferencePropsWithArgs(),
      ref: refs.setReference,
    }),
    getFloatingProps: getFloatingPropsWithArgs,
    getItemProps: getItemPropsWithArgs,
    open,
    search: debouncedInputValue,
    // Immediate (non-debounced) input text, for callers that must not lose
    // trailing characters on a fast save (free-text name fields).
    inputValue,
    setSearch: setInputValue,
    resetSearch,
    resetActiveIndex,
    refs,
    setOpen,
  } as const;
};

export default useAutoComplete;
